import {
  AdvertisingIntent,
  AdvertisingModelOptions,
  AdvertisingPoint,
  AdvertisingSession,
  AdvertisingSnapshot,
} from './types';
import { getAdvertisingMode, getAdvertisingPatch, getAdvertisingSettings, getBindingIdentity } from './utils';

// One model lives at the page provider, including while its drawer is closed.
const createAdvertisingModel = (options: AdvertisingModelOptions) => {
  const sessions = new Map<string, AdvertisingSession>();
  const listeners = new Set<() => void>();
  let isActive = true;

  const notify = () => {
    if (isActive) listeners.forEach((listener) => listener());
  };

  const project = (session: AdvertisingSession) => {
    let settings = { ...session.confirmed, ...session.inFlight };
    session.queue.forEach((intent) => {
      settings = { ...settings, ...getAdvertisingPatch(session.mode, settings, intent) };
    });
    return settings;
  };

  const reconcile = async (pointId: string) => {
    const session = sessions.get(pointId);
    if (!session || session.isReconciling) return;
    // Repeated clicks must not start reads whose late responses can overwrite a later save.
    session.isReconciling = true;
    try {
      const point = await options.read(session.point.id);
      const settings = point && getAdvertisingSettings(point);
      if (point && settings) {
        session.confirmed = settings;
        session.mode = getAdvertisingMode(
          point,
          getBindingIdentity(point) === session.binding ? session.mode : undefined
        );
        session.point = point;
        session.binding = getBindingIdentity(point);
        session.isUncertain = false;
      }
    } catch {
      // Keep the uncertain outcome until a later explicit network read succeeds.
    } finally {
      session.isReconciling = false;
    }
  };

  const drain = async (pointId: string) => {
    const session = sessions.get(pointId);
    if (!session) return;
    if (!isActive || session.inFlight || session.isUncertain) return;
    const intent = session.queue.shift();
    if (!intent) return;
    const patch = getAdvertisingPatch(session.mode, session.confirmed, intent);
    if (!patch) {
      drain(session.point.id);
      notify();
      return;
    }
    session.inFlight = patch;
    notify();

    try {
      const result = await options.save({ pointId: session.point.id, ...patch });
      if (result.status === 'saved') {
        session.confirmed = getAdvertisingSettings(result.point) || { ...session.confirmed, ...patch };
      } else if (result.status === 'rejected') {
        options.onError();
      } else {
        session.isUncertain = true;
        // A timeout is not a cancellation. Never replay this request or queued
        // intentions against an unknown server state.
        session.queue = [];
        await reconcile(session.point.id);
        options.onError();
      }
    } catch {
      session.isUncertain = true;
      session.queue = [];
      await reconcile(session.point.id);
      options.onError();
    } finally {
      session.inFlight = undefined;
      notify();
      if (isActive) drain(session.point.id);
    }
  };

  return {
    sync(point: AdvertisingPoint) {
      const settings = getAdvertisingSettings(point);
      if (!settings) return;
      const binding = getBindingIdentity(point);
      const session = sessions.get(point.id);
      if (session) {
        session.mode = getAdvertisingMode(point, binding === session.binding ? session.mode : undefined);
        session.binding = binding;
        session.point = point;
        if (!session.inFlight && !session.queue.length && !session.isUncertain) {
          session.confirmed = settings;
        }
      } else {
        sessions.set(point.id, {
          point,
          binding,
          confirmed: settings,
          mode: getAdvertisingMode(point),
          queue: [],
          isUncertain: false,
        });
      }
      notify();
    },
    get(pointId?: string): AdvertisingSnapshot | undefined {
      const session = pointId && sessions.get(pointId);
      if (!session) return undefined;
      return {
        pointId: session.point.id,
        mode: session.mode,
        settings: project(session),
        isSaving: !!session.inFlight,
      };
    },
    change(pointId: string, intent: AdvertisingIntent): boolean {
      const session = sessions.get(pointId);
      if (!isActive || !session) return false;
      if (session.isUncertain) {
        reconcile(session.point.id).then(notify);
        options.onError();
        return false;
      }
      if (!getAdvertisingPatch(session.mode, project(session), intent)) return false;
      session.queue.push(intent);
      drain(session.point.id);
      notify();
      return true;
    },
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    activate() {
      isActive = true;
    },
    dispose() {
      isActive = false;
      for (const session of sessions.values()) {
        session.queue = [];
      }
    },
  };
};

export default createAdvertisingModel;
